import React, { useState, useEffect } from "react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Check, Sparkles, ShieldCheck, ArrowLeft } from "lucide-react";
import { DemoSignupModal } from "./demo/DemoSignupModal";
import { trackPricing } from "../lib/visitorTracking";

const FEATURES = [
  "Unlimited sites & projects",
  "Unlimited team members",
  "Daily Site Diary",
  "OH&S — Digital Inductions + Safety Walks",
  "QR Attendance",
  "Defects Management",
  "Client Comms & Variations",
  "Site Photos",
  "Reports & Analytics",
  "Task Management",
  "Calendar View",
  "And so many more....",
];

export default function PublicPricing() {
  const [plans, setPlans] = useState(null);
  const [cycle, setCycle] = useState("annual");
  const [showSignup, setShowSignup] = useState(false);

  useEffect(() => {
    trackPricing();
    fetch(`${API}/billing/plans`).then((r) => r.json()).then(setPlans).catch(() => {});
  }, []);

  const monthly = plans?.monthly;
  const annual = plans?.annual;
  const monthsFree = monthly && annual && monthly.amount ? Math.round((monthly.amount * 12 - annual.amount) / monthly.amount) : 0;
  const annualSaving = monthly && annual ? Math.round(monthly.amount * 12 - annual.amount) : 0;
  const annualPerMonth = annual ? (annual.amount / 12).toFixed(2) : null;

  const isAnnual = cycle === "annual";
  const price = isAnnual ? annual?.amount : monthly?.amount;

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="public-pricing-page">
      {/* top bar */}
      <header className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5 group" data-testid="pricing-logo-home-link" aria-label="Back to SiteProApp home">
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm ring-1 ring-white/20 transition-transform group-hover:scale-105">
            <img src="/logo.png?v=3" alt="Site Pro App" className="w-8 h-8 object-contain" />
          </div>
          <span className="font-bold text-lg tracking-tight">Site Pro App</span>
        </a>
        <a href="/" data-testid="pricing-back-home-btn"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-300 hover:text-white border border-white/15 hover:border-white/35 rounded-full px-4 py-2 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </a>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-10 pb-24 text-center">
        <span className="inline-flex items-center gap-1.5 text-amber-400 text-xs font-semibold uppercase tracking-wider bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-1">
          <Sparkles className="w-3.5 h-3.5" /> One simple plan
        </span>
        <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight">Run your sites for one flat price</h1>
        <p className="mt-4 text-slate-400 text-base sm:text-lg">Everything included. No per-user fees, no surprises. GST inclusive, cancel anytime.</p>

        {/* toggle */}
        <div className="mt-8 inline-flex max-w-full items-center bg-white/5 border border-white/10 rounded-full p-1" data-testid="pricing-cycle-toggle">
          <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly"
            className={`px-4 sm:px-5 py-2 rounded-full text-sm font-semibold transition-colors ${!isAnnual ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"}`}>
            Monthly
          </button>
          <button onClick={() => setCycle("annual")} data-testid="cycle-annual"
            className={`px-4 sm:px-5 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-2 ${isAnnual ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"}`}>
            Annual
            {monthsFree > 0 && <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded-full whitespace-nowrap">{monthsFree} MONTHS FREE</span>}
          </button>
        </div>

        {/* card */}
        <div className="mt-8 relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.07] to-transparent p-6 sm:p-8 text-left shadow-2xl">
          {isAnnual && (
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] sm:text-xs font-bold px-3 py-1 rounded-full shadow whitespace-nowrap" data-testid="pricing-popular-ribbon">
              MOST POPULAR · BEST VALUE
            </span>
          )}
          <div className="text-center">
            {price != null ? (
              <>
                <div className="flex flex-wrap items-end justify-center gap-x-1">
                  <span className="text-4xl sm:text-5xl font-extrabold">${price}</span>
                  <span className="text-slate-400 mb-1.5"> {(isAnnual ? annual : monthly)?.currency?.toUpperCase()} / {isAnnual ? "year" : "month"}</span>
                </div>
                {isAnnual ? (
                  <p className="mt-2 text-emerald-400 text-sm font-medium" data-testid="pricing-annual-note">
                    Save ${annualSaving}/yr — just ${annualPerMonth}/mo, billed yearly
                  </p>
                ) : (
                  <p className="mt-2 text-slate-400 text-sm">Flexible month-to-month</p>
                )}
              </>
            ) : (
              <div className="h-16 animate-pulse bg-white/5 rounded-xl" />
            )}
          </div>

          <ul className="mt-7 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3 text-slate-200">
                <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <span className="text-sm">{f}</span>
              </li>
            ))}
          </ul>

          <Button onClick={() => setShowSignup(true)} data-testid="pricing-start-trial-btn"
            className="mt-8 w-full h-12 text-base bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
            Start your free trial
          </Button>
          <p className="mt-3 text-center text-xs text-slate-500 flex items-center justify-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" /> 30-day free trial · No card required · Cancel anytime
          </p>
        </div>

        {/* FAQ */}
        <div className="mt-12 text-left max-w-2xl mx-auto" data-testid="pricing-faq">
          <h2 className="text-center text-lg font-bold text-white">Frequently asked questions</h2>
          <div className="mt-5 space-y-3">
            {[
              ["Can I cancel anytime?", "Yes. Cancel whenever you like — there are no lock-in contracts. You keep access until the end of your billing period."],
              ["Is GST included?", "Yes. All prices shown are in AUD and are GST inclusive — the price you see is the price you pay."],
              ["Do I need a credit card to start?", "No. The free trial needs no credit card. Add payment only when you're ready to continue."],
              ["What happens when the trial ends?", "Subscribe to keep working with all your data intact. If you don't, your trial data is removed after the trial — no charges."],
              ["How many users and sites can I have?", "Unlimited. One flat price covers all your sites and team members — no per-seat fees."],
            ].map(([q, a]) => (
              <div key={q} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                <p className="font-semibold text-white text-sm">{q}</p>
                <p className="mt-1 text-sm text-slate-400">{a}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Button onClick={() => setShowSignup(true)} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold" data-testid="faq-cta-btn">
              Start your free trial
            </Button>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <img src="/logo.png?v=3" alt="Site Pro App" className="w-6 h-6 object-contain" />
            <span className="font-semibold text-slate-300">Site Pro App</span>
          </div>
          <span>© {new Date().getFullYear()} Site Pro App · Daily site management made simple</span>
        </div>
      </footer>

      <DemoSignupModal open={showSignup} onClose={() => setShowSignup(false)} />
    </div>
  );
}
